/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Button, Spacer } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { AssignMemberSidebar } from "~/components/Team/AssignMemberSidebar";
import { TeamEmployee } from "~/components/Team/types";
import { ACTIONS, Can } from "~/routing";
import { useAxios } from "~/tools";

type AssignTeamMemberProps = {
  refetch: () => void;
  setIsSidebarOpen: (i: boolean) => void;
  isSidebarOpen: boolean;
  team: TeamEmployee[];
};

function AssignTeamMember({
  refetch,
  setIsSidebarOpen,
  isSidebarOpen,
  team,
}: AssignTeamMemberProps) {
  const [roles, setRoles] = React.useState([]);
  const [employees, setEmployees] = React.useState([]);
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const fetchRoles = React.useCallback(async () => {
    try {
      const { data } = await Axios().get("/roles?limit=100");
      setRoles(data?.roles);
    } catch (e) {
      showError(e.response);
    }
  }, [Axios, showError]);

  React.useEffect(() => {
    isSidebarOpen && fetchRoles();
  }, [fetchRoles, isSidebarOpen]);

  const fetchEmployees = React.useCallback(async () => {
    try {
      const { data } = await Axios().get("/search/employees");
      setEmployees(data?.searchResults);
    } catch (e) {
      showError(e.response);
    }
  }, [Axios, showError]);

  React.useEffect(() => {
    isSidebarOpen && fetchEmployees();
  }, [setEmployees, isSidebarOpen, fetchEmployees]);

  return (
    <>
      <Can action={ACTIONS.ADD_CUSTOMER_EMPLOYEE}>
        <Button
          icon="link"
          css={{ marginLeft: "1rem" }}
          onClick={() => setIsSidebarOpen(true)}
        >
          Assign Employee to Customer Team
        </Button>
      </Can>
      <Spacer height="1rem" />
      <AssignMemberSidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        refetch={refetch}
        employees={employees}
        refetchRoles={fetchRoles}
        roles={roles}
        team={team}
      />
    </>
  );
}

export { AssignTeamMember };
